import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from './api';
import Spinner from './Spinner';
import CustomAlert from './CustomAlert';
import HamburgerMenu from './HamburgerMenu';
import { getAircraftModex } from './aircraftModex';

const isT44TmsName = (name) => /t[\s-]*44/i.test(name || '');

const getStyles = (theme) => {
    const isDark = theme === 'dark';
    const colors = {
        background: isDark ? '#1e1e1e' : '#f8f9fa',
        text: isDark ? '#e0e0e0' : '#212529',
        cardBg: isDark ? '#2a2a2a' : '#fff',
        border: isDark ? '#444' : '#dee2e6',
        inputBg: isDark ? '#333' : '#fff',
        link: isDark ? '#6cb2f7' : '#007bff',
        deleteButton: isDark ? '#dc3545' : '#dc3545',
    };

    return {
        container: { maxWidth: '1000px', margin: '40px auto', fontFamily: 'sans-serif', color: colors.text },
        formContainer: { backgroundColor: colors.cardBg, padding: '20px', borderRadius: '8px', border: `1px solid ${colors.border}`, marginBottom: '30px' },
        tableContainer: { backgroundColor: colors.cardBg, padding: '20px', borderRadius: '8px', border: `1px solid ${colors.border}` },
        form: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', alignItems: 'flex-end' },
        formGroup: { display: 'flex', flexDirection: 'column' },
        label: { marginBottom: '5px', fontWeight: 'bold' },
        input: { padding: '10px', borderRadius: '4px', border: `1px solid ${colors.border}`, backgroundColor: colors.inputBg, color: colors.text, fontSize: '16px' },
        button: { padding: '10px 15px', backgroundColor: colors.link, color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold' },
        buttonDisabled: { padding: '10px 15px', backgroundColor: '#ccc', color: '#666', border: 'none', borderRadius: '4px', cursor: 'not-allowed', fontSize: '16px', fontWeight: 'bold' },
        pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
        pageTitle: { fontSize: '24px', margin: 0 },
        menuItem: {
            padding: '10px 15px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            display: 'block',
            textDecoration: 'none',
            color: colors.text,
        },
        menuItemButton: {
            background: 'none',
            border: 'none',
            padding: '10px 15px',
            width: '100%',
            textAlign: 'left',
            cursor: 'pointer',
            fontSize: '16px',
            fontFamily: 'sans-serif',
            color: colors.text,
        },
        table: { width: '100%', borderCollapse: 'collapse' },
        th: { borderBottom: `2px solid ${colors.border}`, padding: '12px', textAlign: 'left', backgroundColor: colors.background },
        td: { borderBottom: `1px solid ${colors.border}`, padding: '12px' },
        deleteButton: { 
            padding: '5px 10px', 
            backgroundColor: colors.deleteButton, 
            color: 'white', 
            border: 'none', 
            borderRadius: '4px', 
            cursor: 'pointer' 
        },
        confirmButton: {
            padding: '5px 10px', 
            backgroundColor: 'darkred', 
            color: 'white', 
            border: 'none', 
            borderRadius: '4px', 
            cursor: 'pointer' 
        },
        editButton: {
            padding: '5px 10px',
            backgroundColor: 'transparent',
            color: colors.link,
            border: `1px solid ${colors.link}`,
            borderRadius: '4px',
            cursor: 'pointer',
            marginRight: '8px'
        },
        saveButton: {
            padding: '5px 10px',
            backgroundColor: colors.link,
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            marginRight: '8px'
        },
        cancelButton: {
            padding: '5px 10px',
            backgroundColor: 'transparent',
            color: colors.text,
            border: `1px solid ${colors.border}`,
            borderRadius: '4px',
            cursor: 'pointer'
        },
        actionsCell: {
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '6px'
        }
    };
};

function AircraftAdminPage({ theme }) {
    const styles = useMemo(() => getStyles(theme), [theme]);
    const [aircraftList, setAircraftList] = useState([]);
    const [sites, setSites] = useState([]);
    const [tmsOptions, setTmsOptions] = useState([]);
    const [formData, setFormData] = useState({ buno: '', tms: '', site: '', modex: '' });
    const [loading, setLoading] = useState(true);
    const [alert, setAlert] = useState({ show: false, message: '', type: '' });
    const [deletingBuno, setDeletingBuno] = useState(null);
    const [editingBuno, setEditingBuno] = useState(null);
    const [editModex, setEditModex] = useState('');
    const [savingModex, setSavingModex] = useState(false);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [statusUploadFile, setStatusUploadFile] = useState(null);
    const [statusUploading, setStatusUploading] = useState(false);
    
    const fetchData = useCallback(() => {
        setLoading(true);
        api.get('/aircraft/')
            .then(res => {
                console.log('Aircraft API response:', res.data);
                
                // Handle paginated response - check if it's paginated or direct array
                let aircraftData = res.data;
                if (res.data && res.data.results) {
                    // Paginated response
                    aircraftData = res.data.results;
                    console.log('Paginated response - total count:', res.data.count);
                }
                
                // The API should already provide tms_name and site_name from the serializer
                // If not, fallback to extracting from nested objects
                const formattedList = Array.isArray(aircraftData) ? aircraftData.map(ac => ({
                    ...ac,
                    tms_name: ac.tms_name || ac.tms?.name || ac.tms || 'Unknown',
                    site_name: ac.site_name || ac.site?.name || ac.site || 'Unknown'
                })) : [];
                console.log('Formatted aircraft list:', formattedList);
                console.log('Number of aircraft in list:', formattedList.length);
                setAircraftList(formattedList);
            })
            .catch(err => {
                console.error("Error fetching aircraft list", err);
                let errorMessage = 'Failed to load aircraft list';
                if (err.response?.status === 401) {
                    errorMessage = 'Authentication required. Please log in again.';
                } else if (err.response?.status === 403) {
                    errorMessage = 'Access denied. You do not have permission to view aircraft.';
                } else if (err.response?.status >= 500) {
                    errorMessage = 'Server error. Please try again later.';
                } else if (err.code === 'NETWORK_ERROR' || !navigator.onLine) {
                    errorMessage = 'Network error. Please check your connection.';
                }
                setAlert({ show: true, message: errorMessage, type: 'error' });
                setAircraftList([]); // Set empty array on error
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        fetchData();
        api.get('/sites/')
            .then(res => {
                console.log('Sites API response:', res.data);
                // Ensure we have an array
                const sitesData = Array.isArray(res.data) ? res.data : (res.data.results || []);
                setSites(sitesData);
            })
            .catch(err => {
                console.error("Error fetching sites", err);
                setSites([]); // Set empty array on error
            });
        api.get('/tms/')
            .then(res => {
                console.log('TMS API response:', res.data);
                // Ensure we have an array
                const tmsData = Array.isArray(res.data) ? res.data : (res.data.results || []);
                setTmsOptions(tmsData);
            })
            .catch(err => {
                console.error("Error fetching TMS options", err);
                setTmsOptions([]); // Set empty array on error
            });
    }, [fetchData]);

    const handleChange = useCallback((e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    }, []);

    const selectedTms = useMemo(
        () => (Array.isArray(tmsOptions) ? tmsOptions.find(t => String(t.id) === String(formData.tms)) : null),
        [tmsOptions, formData.tms]
    );
    const isT44Selected = isT44TmsName(selectedTms?.name);
    const defaultModexPreview = formData.buno.trim().length >= 3 ? formData.buno.trim().slice(-3) : '';

    const isFormValid = useMemo(() => {
        const hasRequired = formData.buno.trim() && formData.tms && formData.site;
        if (!hasRequired) return false;
        if (isT44Selected && !formData.modex.trim()) return false;
        return true;
    }, [formData, isT44Selected]);

    const handleSubmit = (e) => {
        e.preventDefault();
        
        // Check if form is already being submitted
        if (loading) {
            return;
        }
        
        // Validate form data
        if (!formData.buno.trim()) {
            setAlert({ show: true, message: 'BUNO is required.', type: 'error' });
            return;
        }
        
        // Check if BUNO already exists in the current list
        const bunoExists = aircraftList.some(aircraft => 
            aircraft.buno.toLowerCase() === formData.buno.trim().toLowerCase()
        );
        if (bunoExists) {
            setAlert({ show: true, message: 'An aircraft with this BUNO already exists.', type: 'error' });
            return;
        }
        
        if (!formData.tms) {
            setAlert({ show: true, message: 'Please select a TMS.', type: 'error' });
            return;
        }
        
        if (!formData.site) {
            setAlert({ show: true, message: 'Please select a Site.', type: 'error' });
            return;
        }

        if (isT44Selected && !formData.modex.trim()) {
            setAlert({ show: true, message: 'MODEX is required for T-44 aircraft.', type: 'error' });
            return;
        }
        
        setLoading(true);
        const submissionData = {
            buno: formData.buno.trim(),
            tms: formData.tms,
            site: formData.site,
            modex: formData.modex.trim()
        };

        console.log('Submitting aircraft data:', submissionData);

        api.post('/aircraft/', submissionData)
            .then((response) => {
                console.log('Aircraft added successfully:', response.data);
                setAlert({ show: true, message: 'Aircraft added successfully!', type: 'success' });
                setFormData({ buno: '', tms: '', site: '', modex: '' }); // Clear form
                fetchData(); // Refresh the list
            })
            .catch(err => {
                console.error('Error adding aircraft:', err);
                console.error('Error response:', err.response?.data);
                
                let errorMsg = 'Failed to add aircraft.';
                if (err.response?.data) {
                    if (err.response.data.buno) {
                        errorMsg = err.response.data.buno[0];
                    } else if (err.response.data.detail) {
                        errorMsg = err.response.data.detail;
                    } else if (err.response.data.non_field_errors) {
                        errorMsg = err.response.data.non_field_errors[0];
                    } else if (err.response.data.tms) {
                        errorMsg = `TMS Error: ${err.response.data.tms[0]}`;
                    } else if (err.response.data.site) {
                        errorMsg = `Site Error: ${err.response.data.site[0]}`;
                    } else if (err.response.data.modex) {
                        errorMsg = `MODEX Error: ${err.response.data.modex[0]}`;
                    } else {
                        // Try to extract meaningful error from the response
                        const errorKeys = Object.keys(err.response.data);
                        if (errorKeys.length > 0) {
                            const firstError = err.response.data[errorKeys[0]];
                            if (Array.isArray(firstError)) {
                                errorMsg = firstError[0];
                            } else {
                                errorMsg = firstError;
                            }
                        } else {
                            errorMsg = 'An unexpected error occurred. Please try again.';
                        }
                    }
                } else if (err.message) {
                    errorMsg = err.message;
                }
                
                setAlert({ show: true, message: errorMsg, type: 'error' });
            })
            .finally(() => setLoading(false));
    };

    const handleDelete = (buno) => {
        setLoading(true);
        api.delete(`/aircraft/${buno}/`)
            .then(() => {
                setAlert({ show: true, message: `Aircraft ${buno} deleted successfully.`, type: 'success' });
                fetchData(); // Refresh the list
            })
            .catch(err => {
                setAlert({ show: true, message: 'Failed to delete aircraft.', type: 'error' });
            })
            .finally(() => {
                setLoading(false);
                setDeletingBuno(null); // Reset confirmation state
            });
    };

    const startEditModex = (aircraft) => {
        setDeletingBuno(null);
        setEditingBuno(aircraft.buno);
        setEditModex(aircraft.modex || '');
    };

    const cancelEditModex = () => {
        setEditingBuno(null);
        setEditModex('');
    };

    const handleSaveModex = (aircraft) => {
        const tmsName = aircraft.tms_name || '';
        if (isT44TmsName(tmsName) && !editModex.trim()) {
            setAlert({ show: true, message: 'MODEX is required for T-44 aircraft.', type: 'error' });
            return;
        }
        setSavingModex(true);
        api.patch(`/aircraft/${encodeURIComponent(aircraft.buno)}/`, { modex: editModex.trim() })
            .then(() => {
                setAlert({ show: true, message: `MODEX updated for ${aircraft.buno}.`, type: 'success' });
                setEditingBuno(null);
                setEditModex('');
                fetchData();
            })
            .catch(err => {
                const detail = err.response?.data?.modex?.[0] || err.response?.data?.detail || 'Failed to update MODEX.';
                setAlert({ show: true, message: detail, type: 'error' });
            })
            .finally(() => setSavingModex(false));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setUploadFile(file);
    };

    const handleStatusFileChange = (e) => {
        const file = e.target.files[0];
        setStatusUploadFile(file);
    };

    const handleBulkUpload = (e) => {
        e.preventDefault();
        
        if (!uploadFile) {
            setAlert({ show: true, message: 'Please select a file to upload.', type: 'error' });
            return;
        }

        // Validate file type
        const allowedTypes = ['.csv', '.xlsx', '.xls'];
        const fileExtension = uploadFile.name.toLowerCase().substring(uploadFile.name.lastIndexOf('.'));
        if (!allowedTypes.includes(fileExtension)) {
            setAlert({ show: true, message: 'Please select a CSV or Excel file (.csv, .xlsx, .xls).', type: 'error' });
            return;
        }

        setUploading(true);
        const formData = new FormData();
        formData.append('file', uploadFile);

        api.post('/upload-aircraft/', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        })
        .then((response) => {
            console.log('Bulk upload response:', response.data);
            const { created_count, updated_count, skipped_count, errors, error_count, debug_info, total_aircraft } = response.data;
            let message = `Bulk upload completed! Created: ${created_count} aircraft`;
            if (updated_count > 0) {
                message += `, Updated MODEX: ${updated_count}`;
            }
            if (skipped_count > 0) {
                message += `, Skipped: ${skipped_count} (duplicates)`;
            }
            if (error_count > 0) {
                message += `, Errors: ${error_count}`;
            }
            
            // Determine alert type based on results
            let alertType = 'success';
            if (created_count === 0 && error_count === 0) {
                alertType = 'warning';
                message = 'No aircraft were created. Please check your file format and data.';
            } else if (error_count > 0) {
                alertType = 'warning';
            }
            if (total_aircraft !== undefined) {
                message += ` (Total aircraft in database: ${total_aircraft})`;
            }
            console.log('Upload response details:', { created_count, skipped_count, total_aircraft });
            
            setAlert({ 
                show: true, 
                message: message, 
                type: alertType 

            });
            
            // Show detailed errors if any
            if (errors && errors.length > 0) {
                console.warn('Upload errors:', errors);
                setTimeout(() => {
                    const errorMessage = errors.length > 3 
                        ? `First few errors: ${errors.slice(0, 3).join('; ')}... (${errors.length} total)`
                        : `Errors: ${errors.join('; ')}`;
                    setAlert({ 
                        show: true, 

                        message: errorMessage, 
                        type: 'error' 

                    });
                }, 2000);
            }
            

            // Show debug info if no aircraft were created
            if (debug_info) {
                console.log('Debug info:', debug_info);
            }
            setUploadFile(null);
            document.getElementById('bulk-upload-file').value = ''; // Clear file input
            
            // Add a small delay to ensure the database transaction is committed
            setTimeout(() => {
                fetchData(); // Refresh the aircraft list
            }, 500);
        })
        .catch(err => {
            console.error('Bulk upload error:', err);
            let errorMsg = 'Failed to upload aircraft file.';
            if (err.response?.data?.error) {
                errorMsg = err.response.data.error;
            } else if (err.response?.data?.detail) {
                errorMsg = err.response.data.detail;
            }
            setAlert({ show: true, message: errorMsg, type: 'error' });
        })
        .finally(() => setUploading(false));
    };

    const handleBulkStatusUpload = (e) => {
        e.preventDefault();
        
        if (!statusUploadFile) {
            setAlert({ show: true, message: 'Please select a file to upload.', type: 'error' });
            return;
        }

        // Validate file type
        const allowedTypes = ['.csv', '.xlsx', '.xls'];
        const fileExtension = statusUploadFile.name.toLowerCase().substring(statusUploadFile.name.lastIndexOf('.'));
        if (!allowedTypes.includes(fileExtension)) {
            setAlert({ show: true, message: 'Please select a CSV or Excel file (.csv, .xlsx, .xls).', type: 'error' });
            return;
        }

        setStatusUploading(true);
        const formData = new FormData();
        formData.append('file', statusUploadFile);

        api.post('/upload-aircraft-status/', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        })
        .then((response) => {
            console.log('Bulk status upload response:', response.data);
            const { updated_count, skipped_count, errors, error_count } = response.data;
            let message = `Bulk status update completed! Updated: ${updated_count} aircraft`;
            if (skipped_count > 0) {
                message += `, Skipped: ${skipped_count}`;
            }
            if (error_count > 0) {
                message += `, Errors: ${error_count}`;
            }
            
            // Determine alert type based on results
            let alertType = 'success';
            if (updated_count === 0 && error_count === 0) {
                alertType = 'warning';
                message = 'No aircraft statuses were updated. Please check your file format and data.';
            } else if (error_count > 0) {
                alertType = 'warning';
            }
            
            setAlert({ 
                show: true, 
                message: message, 
                type: alertType 
            });
            
            // Show detailed errors if any
            if (errors && errors.length > 0) {
                console.warn('Status upload errors:', errors);
                setTimeout(() => {
                    const errorMessage = errors.length > 3 
                        ? `First few errors: ${errors.slice(0, 3).join('; ')}... (${errors.length} total)`
                        : `Errors: ${errors.join('; ')}`;
                    setAlert({ 
                        show: true, 
                        message: errorMessage, 
                        type: 'error' 
                    });
                }, 2000);
            }
            
            setStatusUploadFile(null);
            document.getElementById('status-upload-file').value = ''; // Clear file input
            
            // Add a small delay to ensure the database transaction is committed
            setTimeout(() => {
                fetchData(); // Refresh the aircraft list
            }, 500);
        })
        .catch(err => {
            console.error('Bulk status upload error:', err);
            let errorMsg = 'Failed to upload status file.';
            if (err.response?.data?.error) {
                errorMsg = err.response.data.error;
            } else if (err.response?.data?.detail) {
                errorMsg = err.response.data.detail;
            }
            setAlert({ show: true, message: errorMsg, type: 'error' });
        })
        .finally(() => setStatusUploading(false));
    };


    return (
        <div style={styles.container}>
            <CustomAlert {...alert} onClose={() => setAlert({ show: false })} theme={theme} />
            <div style={styles.pageHeader}>
                <HamburgerMenu theme={theme}/>
                <h2 style={styles.pageTitle}>Aircraft Administration</h2>
                <div style={{ width: '45px' }}></div>
            </div>
            
            <div style={styles.formContainer}>
                <h2>Add New Aircraft</h2>
                <form onSubmit={handleSubmit} style={styles.form}>
                    <div style={styles.formGroup}>
                        <label htmlFor="buno" style={styles.label}>BUNO *</label>
                        <input 
                            id="buno" 
                            name="buno" 
                            type="text" 
                            value={formData.buno} 
                            onChange={handleChange} 
                            style={styles.input} 
                            required 
                            aria-describedby="buno-help"
                            placeholder="Enter aircraft BUNO"
                        />
                        <small id="buno-help" style={{ fontSize: '12px', color: styles.container.color, opacity: 0.7 }}>
                            Aircraft Bureau Number (required)
                        </small>
                    </div>
                    <div style={styles.formGroup}>
                        <label htmlFor="modex" style={styles.label}>MODEX {isT44Selected ? '*' : ''}</label>
                        <input 
                            id="modex" 
                            name="modex" 
                            type="text" 
                            value={formData.modex} 
                            onChange={handleChange} 
                            style={styles.input} 
                            required={isT44Selected}
                            maxLength={10}
                            aria-describedby="modex-help"
                            placeholder={isT44Selected ? 'Required for T-44' : (defaultModexPreview || 'Last 3 of BUNO')}
                        />
                        <small id="modex-help" style={{ fontSize: '12px', color: styles.container.color, opacity: 0.7 }}>
                            {isT44Selected
                                ? 'T-44 MODEX is not the last 3 of BUNO — enter the tail number (e.g. 462).'
                                : (defaultModexPreview
                                    ? `Leave blank to use ${defaultModexPreview} (last 3 of BUNO). Enter a value for T-44s.`
                                    : 'Leave blank to use the last 3 digits of BUNO. Enter a value for T-44s.')}
                        </small>
                    </div>
                    <div style={styles.formGroup}>
                        <label htmlFor="tms" style={styles.label}>TMS *</label>
                        <select 
                            id="tms" 
                            name="tms" 
                            value={formData.tms} 
                            onChange={handleChange} 
                            style={styles.input} 
                            required
                            aria-describedby="tms-help"
                        >
                            <option value="">Select TMS</option>
                            {Array.isArray(tmsOptions) && tmsOptions.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                        <small id="tms-help" style={{ fontSize: '12px', color: styles.container.color, opacity: 0.7 }}>
                            Select a TMS system (required)
                        </small>
                    </div>
                    <div style={styles.formGroup}>
                        <label htmlFor="site" style={styles.label}>Site *</label>
                        <select 
                            id="site" 
                            name="site" 
                            value={formData.site} 
                            onChange={handleChange} 
                            style={styles.input} 
                            required
                            aria-describedby="site-help"
                        >
                            <option value="">Select Site</option>
                            {Array.isArray(sites) && sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                        <small id="site-help" style={{ fontSize: '12px', color: styles.container.color, opacity: 0.7 }}>
                            Select a site location (required)
                        </small>
                    </div>
                    <button 
                        type="submit" 
                        style={loading || !isFormValid ? styles.buttonDisabled : styles.button} 
                        disabled={loading || !isFormValid}
                        aria-label={loading ? 'Adding aircraft, please wait' : 'Add new aircraft to the system'}
                    >
                        {loading ? 'Adding...' : 'Add Aircraft'}
                    </button>
                </form>
            </div>

            <div style={styles.formContainer}>
                <h2>Bulk Upload Aircraft</h2>
                <p style={{ marginBottom: '20px', color: styles.text, fontSize: '14px' }}>
                    Upload multiple aircraft at once using a CSV or Excel file. 
                    The file should have columns: <strong>BUNO, TMS, Site</strong>.
                    Optional column: <strong>Modex</strong> (required for T-44s, whose MODEX is not the last 3 of BUNO).
                    Re-uploading an existing BUNO with a Modex value will update that aircraft's MODEX.
                </p>
                <form onSubmit={handleBulkUpload} style={{ display: 'flex', gap: '15px', alignItems: 'flex-end' }}>
                    <div style={styles.formGroup}>
                        <label htmlFor="bulk-upload-file" style={styles.label}>Select File *</label>
                        <input 
                            id="bulk-upload-file"
                            type="file" 
                            accept=".csv,.xlsx,.xls"
                            onChange={handleFileChange}
                            style={styles.input}
                            disabled={uploading}
                            aria-describedby="bulk-upload-help"
                        />
                        <small id="bulk-upload-help" style={{ fontSize: '12px', color: styles.container.color, opacity: 0.7 }}>
                            CSV or Excel files only (.csv, .xlsx, .xls)
                        </small>
                    </div>
                    <button 
                        type="submit" 
                        style={uploading ? styles.buttonDisabled : styles.button} 
                        disabled={uploading || !uploadFile}
                        aria-label={uploading ? 'Uploading aircraft file, please wait' : 'Upload aircraft from file'}
                    >
                        {uploading ? 'Uploading...' : 'Upload Aircraft'}
                    </button>
                </form>
                {uploadFile && (
                    <div style={{ marginTop: '10px', fontSize: '14px', color: styles.text }}>
                        Selected file: <strong>{uploadFile.name}</strong>
                    </div>
                )}
            </div>

            <div style={styles.formContainer}>
                <h2>Bulk Upload Aircraft Status</h2>
                <p style={{ marginBottom: '20px', color: styles.text, fontSize: '14px' }}>
                    Upload a CSV or Excel file to update aircraft status information in bulk.
                </p>
                <div style={{ marginBottom: '15px', fontSize: '14px', color: styles.text }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Required Column: BUNO</div>
                    <div style={{ marginBottom: '10px' }}>Optional Columns: Status, Reason, EDD, ECD, Last_Fly_Date, Flight_Hours, PM_Status</div>
                    <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Valid Status Values:</div>
                    <div style={{ marginBottom: '10px' }}>UP, XC, DN, DS, PM, FCF, AWP, OTH, DET, AIMD, ACI, REI</div>
                    <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Date Format:</div>
                    <div>YYYY-MM-DD (e.g., 2024-01-15) or any standard date format</div>
                </div>
                <form onSubmit={handleBulkStatusUpload} style={{ display: 'flex', gap: '15px', alignItems: 'flex-end' }}>
                    <div style={styles.formGroup}>
                        <label htmlFor="status-upload-file" style={styles.label}>Select Status File *</label>
                        <input 
                            id="status-upload-file"
                            type="file" 
                            accept=".csv,.xlsx,.xls"
                            onChange={handleStatusFileChange}
                            style={styles.input}
                            disabled={statusUploading}
                            aria-describedby="status-upload-help"
                        />
                        <small id="status-upload-help" style={{ fontSize: '12px', color: styles.container.color, opacity: 0.7 }}>
                            CSV or Excel files only (.csv, .xlsx, .xls)
                        </small>
                    </div>
                    <button 
                        type="submit" 
                        style={statusUploading ? styles.buttonDisabled : styles.button} 
                        disabled={statusUploading || !statusUploadFile}
                        aria-label={statusUploading ? 'Uploading status file, please wait' : 'Upload aircraft status from file'}
                    >
                        {statusUploading ? 'Uploading...' : 'Upload Status'}
                    </button>
                </form>
                {statusUploadFile && (
                    <div style={{ marginTop: '10px', fontSize: '14px', color: styles.text }}>
                        Selected file: <strong>{statusUploadFile.name}</strong>
                    </div>
                )}
            </div>

            <div style={styles.tableContainer}>
                <h2>Existing Aircraft</h2>
                {loading ? <Spinner theme={theme} /> : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={styles.table} role="table" aria-label="Aircraft list">
                            <thead>
                                <tr>
                                    <th style={styles.th} scope="col">BUNO</th>
                                    <th style={styles.th} scope="col">MODEX</th>
                                    <th style={styles.th} scope="col">TMS</th>
                                    <th style={styles.th} scope="col">Site</th>
                                    <th style={styles.th} scope="col">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Array.isArray(aircraftList) && aircraftList.length > 0 ? aircraftList.map(ac => (
                                    <tr key={ac.buno}>
                                        <td style={styles.td}>{ac.buno}</td>
                                        <td style={styles.td}>
                                            {editingBuno === ac.buno ? (
                                                <input
                                                    type="text"
                                                    value={editModex}
                                                    onChange={(e) => setEditModex(e.target.value)}
                                                    style={{ ...styles.input, maxWidth: '110px', padding: '6px' }}
                                                    maxLength={10}
                                                    placeholder={getAircraftModex(ac) || 'MODEX'}
                                                    aria-label={`MODEX for ${ac.buno}`}
                                                />
                                            ) : (
                                                getAircraftModex(ac) || '—'
                                            )}
                                        </td>
                                        <td style={styles.td}>{ac.tms_name}</td>
                                        <td style={styles.td}>{ac.site_name}</td>
                                        <td style={styles.td}>
                                            {editingBuno === ac.buno ? (
                                                <div style={styles.actionsCell}>
                                                    <button
                                                        onClick={() => handleSaveModex(ac)}
                                                        style={styles.saveButton}
                                                        disabled={savingModex}
                                                        aria-label={`Save MODEX for aircraft ${ac.buno}`}
                                                    >
                                                        {savingModex ? 'Saving...' : 'Save'}
                                                    </button>
                                                    <button
                                                        onClick={cancelEditModex}
                                                        style={styles.cancelButton}
                                                        disabled={savingModex}
                                                        aria-label="Cancel MODEX edit"
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            ) : deletingBuno === ac.buno ? (
                                                <button 
                                                    onClick={() => handleDelete(ac.buno)} 
                                                    style={styles.confirmButton}
                                                    aria-label={`Confirm deletion of aircraft ${ac.buno}`}
                                                >
                                                    Confirm?
                                                </button>
                                            ) : (
                                                <div style={styles.actionsCell}>
                                                    <button
                                                        onClick={() => startEditModex(ac)}
                                                        style={styles.editButton}
                                                        aria-label={`Edit MODEX for aircraft ${ac.buno}`}
                                                    >
                                                        Edit MODEX
                                                    </button>
                                                    <button 
                                                        onClick={() => setDeletingBuno(ac.buno)} 
                                                        style={styles.deleteButton}
                                                        aria-label={`Delete aircraft ${ac.buno}`}
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan="5" style={{ ...styles.td, textAlign: 'center', fontStyle: 'italic' }}>
                                            No aircraft found. Add some aircraft using the form above.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

export default AircraftAdminPage;

